/**
 * permissions.js — FE single source of truth for profile-based access checks.
 *
 * Parallel to backend/services/permission_service.py (PR-D). Same canonical
 * codes, same LEGACY → CANONICAL map, same helper semantics. Keeping them
 * synchronised is part of the migration contract — see the audit at
 * memory/project_user_profile_audit_2026-05-28.md.
 *
 * This PR (PR-E) is ADDITIVE only — zero behaviour change. Future PRs
 * migrate the existing hardcoded checks (PermissionsContext.js,
 * PaymentModal.js, App.js scattered checks) over to these helpers.
 */

// ---------------------------------------------------------------------------
// Canonical profile codes — MUST mirror permission_service.py:CANONICAL_PROFILES.
// ---------------------------------------------------------------------------

export const CANONICAL_PROFILES = new Set([
  'Admin',
  'Standard User',
  'senior_management',
  'management',
  'office_management',
  'accounting',
  'secretariat',
  'reminders',
  'call_center_manager',
  'sales_center_manager',
  'service_center_manager',
  'sales_center',
  'service_center',
  'initiation_center',
  'collections_center',
  'institutional',
  'distributors_management',
  'distributor',
  'warehouse_management',
  'lab_manager',
  'lab_technician',
  'lab_member',
  'technician',
  'driver',
]);

// ---------------------------------------------------------------------------
// Legacy / variant → canonical map. Identical to backend's
// LEGACY_TO_CANONICAL — keep in sync.
// ---------------------------------------------------------------------------

export const LEGACY_TO_CANONICAL = {
  // Admin variants
  'system_admin': 'Admin',
  'מנהל מערכת': 'Admin',
  // Hebrew label twins of canonical English codes
  'הנהלה': 'management',
  'הנהלה בכירה': 'senior_management',
  'הנהלת מוקד': 'call_center_manager',
  'הנהלת מוקד מכירה': 'sales_center_manager',
  'הנהלת מוקד שירות': 'service_center_manager',
  'הנהלה מוקד שירות': 'service_center_manager',
  'הנהלת מחסן': 'warehouse_management',
  'הנהלת חשבונות': 'accounting',
  'הנהלת מעבדה': 'lab_manager',
  'הנהלת משווקים': 'distributors_management',
  'הנהלת משרד': 'office_management',
  'מזכירות': 'secretariat',
  'תזכורים': 'reminders',
  'מוסדי': 'institutional',
  'משווקים': 'distributor',
  'מוקד מכירה': 'sales_center',
  'מוקד שירות': 'service_center',
  'מוקד יזום': 'initiation_center',
  'מוקד גביה': 'collections_center',
  'מעבדה': 'lab_member',
  'מנהל מעבדה': 'lab_manager',
  'טכנאי מעבדה': 'lab_technician',
  // Technician collapse — all map to 'technician'. Sales-vs-service is
  // on users.technical_type, NOT profile.
  'טכנאים': 'technician',
  'טכנאי': 'technician',
  'טכנאי שירות': 'technician',
  'טכנאי שרות': 'technician',
  'טכנאי מכירה': 'technician',
  'טכנאי מכירות': 'technician',
  'sales_technician': 'technician',
  'service_technician': 'technician',
  // Legacy typo
  'lab_menager': 'lab_manager',
  // Driver (dealer REV-2 P5). Its own category — not a technician.
  'נהג': 'driver',
};

// ---------------------------------------------------------------------------
// Category sets — admin is a superset of every category.
// ---------------------------------------------------------------------------

export const ADMIN_PROFILES = new Set(['Admin']);

export const MANAGER_PROFILES = new Set([
  ...ADMIN_PROFILES,
  'senior_management',
  'management',
  'office_management',
  'accounting',
  'call_center_manager',
  'sales_center_manager',
  'service_center_manager',
  'warehouse_management',
  'distributors_management',
  'lab_manager',
]);

export const ACCOUNTING_PROFILES = new Set([...ADMIN_PROFILES, 'accounting']);

export const LAB_PROFILES = new Set([
  ...ADMIN_PROFILES,
  'lab_manager',
  'lab_technician',
  'lab_member',
]);

export const TECHNICIAN_PROFILES = new Set(['technician']);

export const DRIVER_PROFILES = new Set(['driver']);

// 2026-06-08 owner-spec: institutional users (B2B bulk-order desk) get
// carve-outs on the signed-engagement-contract Closed-Won gate and on
// the price-range gate in the Add-Products / OLI-edit flow.
export const INSTITUTIONAL_PROFILES = new Set([...ADMIN_PROFILES, 'institutional']);

// 2026-09-02 owner-spec: «יצירת נכס» on Account and «יצירת חוזה שירות» on Asset
// were narrowed to accounting + admin on 2026-09-01 (PR #1644). The owner has
// now added institutional and management to that carve-out.
//
// ⚠️ Deliberately NOT widening ACCOUNTING_PROFILES. `isAccounting` also gates
// «מסמכי לקוח» and both «הפקת מסמכים» buttons — the customer ledger and document
// issuance — and those are financial, not creation. Widening the role would have
// handed institutional and management three buttons nobody asked them to have.
//
// Named for the capability rather than the role, so the next person adding a
// profile can see exactly which two buttons they are opening.
export const ASSET_AND_CONTRACT_PROFILES = new Set([
  ...ACCOUNTING_PROFILES,
  'institutional',
  'management',
]);

export const CALL_CENTER_PROFILES = new Set([
  'sales_center',
  'service_center',
  'initiation_center',
  'collections_center',
  'secretariat',
  'reminders',
]);

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Return the canonical profile code for `value`.
 *   - Empty / null / undefined → ''.
 *   - Known legacy variant → canonical.
 *   - Already canonical → unchanged.
 *   - Unknown → pass-through (so new profiles don't break).
 */
export function canonicalizeProfile(value) {
  if (value == null) return '';
  const v = String(value).trim();
  if (!v) return '';
  return LEGACY_TO_CANONICAL[v] || v;
}

function _extractProfile(user) {
  if (!user || typeof user !== 'object') return '';
  return canonicalizeProfile(user.profile || user.Profile || '');
}

function _flagTrue(user, ...keys) {
  if (!user || typeof user !== 'object') return false;
  for (const k of keys) {
    const v = user[k];
    if (v === true) return true;
    if (typeof v === 'string' && /^(true|1|yes)$/i.test(v.trim())) return true;
  }
  return false;
}

/**
 * True if the user is a system admin. Honours BOTH the canonical profile
 * AND the standalone isAdmin/is_admin boolean — they sometimes disagree
 * (audit finding 2026-05-28). Safe OR is the correct semantic.
 */
export function isAdmin(user) {
  if (_flagTrue(user, 'isAdmin', 'is_admin')) return true;
  return ADMIN_PROFILES.has(_extractProfile(user));
}

export function isInCategory(user, categorySet) {
  if (isAdmin(user)) return true;
  return categorySet.has(_extractProfile(user));
}

export function isManager(user) {
  return isInCategory(user, MANAGER_PROFILES);
}

export function isAccounting(user) {
  return isInCategory(user, ACCOUNTING_PROFILES);
}

export function isLabMember(user) {
  return isInCategory(user, LAB_PROFILES);
}

export function isTechnician(user) {
  return isInCategory(user, TECHNICIAN_PROFILES);
}

// Strict (admins are not drivers): it RESTRICTS a user to delivery activities.
export function isDriver(user) {
  return DRIVER_PROFILES.has(_extractProfile(user));
}

export function isCallCenterAgent(user) {
  return isInCategory(user, CALL_CENTER_PROFILES);
}

export function isInstitutional(user) {
  return isInCategory(user, INSTITUTIONAL_PROFILES);
}

/**
 * May this user create an Asset on an Account, or a Service Contract on an
 * Asset? Accounting and admin (PR #1644), plus institutional and management
 * (owner, 2026-09-02).
 *
 * Display only. `/api/objects/Asset/records` has no matching server-side gate,
 * so a direct call still works — unchanged by this, and called out in #1644 for
 * the same reason.
 */
export function canCreateAssetOrContract(user) {
  return isInCategory(user, ASSET_AND_CONTRACT_PROFILES);
}

// Owner-spec 2026-06-10: distributors (משווקים) are exempt from the
// Closed-Won signed-engagement-contract validation, and the
// "device-at-customer" checkbox in the products step is distributor-only.
// Strict equality (not a category) — admin path goes through isAdmin.
export function isDistributor(user) {
  return _extractProfile(user) === 'distributor';
}

export function hasProfile(user, canonicalCode) {
  if (!canonicalCode) return false;
  return _extractProfile(user) === canonicalCode;
}

export function hasAnyProfile(user, canonicalCodes) {
  const p = _extractProfile(user);
  if (!p) return false;
  const set = canonicalCodes instanceof Set ? canonicalCodes : new Set(canonicalCodes);
  return set.has(p);
}

// Default export so consumers can do `import perms from '...'`.
const permissions = {
  CANONICAL_PROFILES,
  LEGACY_TO_CANONICAL,
  ADMIN_PROFILES,
  MANAGER_PROFILES,
  ACCOUNTING_PROFILES,
  LAB_PROFILES,
  TECHNICIAN_PROFILES,
  DRIVER_PROFILES,
  CALL_CENTER_PROFILES,
  INSTITUTIONAL_PROFILES,
  canonicalizeProfile,
  isAdmin,
  isInCategory,
  isManager,
  isAccounting,
  isLabMember,
  isTechnician,
  isDriver,
  isCallCenterAgent,
  isInstitutional,
  canCreateAssetOrContract,
  hasProfile,
  hasAnyProfile,
};

export default permissions;
